import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { toast } from "react-toastify";
import SuperAdminPageHeader from "./SuperAdminPageHeader";
import { useAdminPanel } from "./hooks/useAdminPanel";
import {
  formatDisplayDateTime,
  getSubscriptionPlans,
  updateSubscriptionPlan,
} from "../../api/superAdminApi";

const TABLE_HEADERS = [
  "#",
  "Plan",
  "Code",
  "Users",
  "Monthly (INR)",
  "Yearly (INR)",
  "Monthly (USD)",
  "Yearly (USD)",
  "Popular",
  "Active",
  "Action",
];

const formatAmount = (value, symbol) => {
  if (value == null || value === "") return "—";
  const locale = symbol === "₹" ? "en-IN" : "en-US";
  return `${symbol}${Number(value).toLocaleString(locale)}`;
};

const planToForm = (plan) => ({
  display_name: plan?.display_name ?? "",
  max_users: plan?.is_unlimited_users ? "" : plan?.max_users ?? "",
  is_unlimited_users: Boolean(plan?.is_unlimited_users),
  is_most_popular: Boolean(plan?.is_most_popular),
  sort_order: plan?.sort_order ?? 0,
  is_active: plan?.is_active !== false,
  monthly_amount_inr: plan?.monthly_amount_inr ?? "",
  yearly_amount_inr: plan?.yearly_amount_inr ?? "",
  monthly_amount_usd: plan?.monthly_amount_usd ?? "",
  yearly_amount_usd: plan?.yearly_amount_usd ?? "",
});

const validateForm = (form) => {
  if (!form.display_name?.trim()) return "Display name cannot be empty.";
  if (form.is_unlimited_users) {
    if (form.max_users !== "" && form.max_users != null) {
      return "Max users must be empty when unlimited users is enabled.";
    }
  } else {
    const max = Number(form.max_users);
    if (!Number.isInteger(max) || max <= 0) {
      return "Max users must be a positive integer when not unlimited.";
    }
  }
  const sortOrder = Number(form.sort_order);
  if (!Number.isInteger(sortOrder) || sortOrder < 0) {
    return "Sort order must be an integer ≥ 0.";
  }
  const amounts = [
    ["Monthly INR", form.monthly_amount_inr],
    ["Yearly INR", form.yearly_amount_inr],
    ["Monthly USD", form.monthly_amount_usd],
    ["Yearly USD", form.yearly_amount_usd],
  ];
  for (const [label, val] of amounts) {
    const n = Number(val);
    if (val === "" || Number.isNaN(n) || n < 0) {
      return `${label} must be a number ≥ 0.`;
    }
  }
  return null;
};

const buildPayload = (form) => ({
  display_name: form.display_name.trim(),
  max_users: form.is_unlimited_users ? null : Number(form.max_users),
  is_unlimited_users: form.is_unlimited_users,
  is_most_popular: form.is_most_popular,
  sort_order: Number(form.sort_order),
  is_active: form.is_active,
  monthly_amount_inr: Number(form.monthly_amount_inr),
  yearly_amount_inr: Number(form.yearly_amount_inr),
  monthly_amount_usd: Number(form.monthly_amount_usd),
  yearly_amount_usd: Number(form.yearly_amount_usd),
});

function PlanEditDialog({ open, plan, readOnly, onClose, onSaved }) {
  const [form, setForm] = useState(planToForm(plan));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && plan) {
      setForm(planToForm(plan));
    }
  }, [open, plan]);

  const handleClose = () => {
    if (!saving) onClose();
  };

  const setField = (key, value) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "is_unlimited_users" && value) {
        next.max_users = "";
      }
      return next;
    });
  };

  const handleSave = async () => {
    const error = validateForm(form);
    if (error) {
      toast.error(error);
      return;
    }
    setSaving(true);
    try {
      const res = await updateSubscriptionPlan(plan.id, buildPayload(form));
      const body = res.data;
      if (body?.status_code === 200) {
        toast.success(body.message || "Subscription plan updated successfully.");
        onSaved?.(body.data);
        onClose();
      } else {
        toast.error(body?.message || "Failed to update plan.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update plan.");
    } finally {
      setSaving(false);
    }
  };

  if (!plan) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle sx={{ pr: 6 }}>
        {readOnly ? "View plan" : "Edit plan"}
        <IconButton
          aria-label="Close"
          onClick={handleClose}
          disabled={saving}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Plan code (read-only)
            </Typography>
            <Chip label={plan.plan_code} size="small" sx={{ mt: 0.5 }} />
          </Box>

          <TextField
            fullWidth
            size="small"
            label="Display name"
            value={form.display_name}
            onChange={(e) => setField("display_name", e.target.value)}
            disabled={readOnly || saving}
            required
          />

          <FormControlLabel
            control={
              <Switch
                checked={form.is_unlimited_users}
                onChange={(e) => setField("is_unlimited_users", e.target.checked)}
                disabled={readOnly || saving}
              />
            }
            label="Unlimited users"
          />

          <TextField
            fullWidth
            size="small"
            type="number"
            label="Max users"
            value={form.max_users}
            onChange={(e) => setField("max_users", e.target.value)}
            disabled={readOnly || saving || form.is_unlimited_users}
            helperText={form.is_unlimited_users ? "Not used when unlimited is enabled" : "Positive integer"}
            inputProps={{ min: 1, step: 1 }}
          />

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Monthly (INR)"
                value={form.monthly_amount_inr}
                onChange={(e) => setField("monthly_amount_inr", e.target.value)}
                disabled={readOnly || saving}
                inputProps={{ min: 0, step: 1 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Yearly (INR)"
                value={form.yearly_amount_inr}
                onChange={(e) => setField("yearly_amount_inr", e.target.value)}
                disabled={readOnly || saving}
                inputProps={{ min: 0, step: 1 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Monthly (USD)"
                value={form.monthly_amount_usd}
                onChange={(e) => setField("monthly_amount_usd", e.target.value)}
                disabled={readOnly || saving}
                inputProps={{ min: 0, step: 1 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Yearly (USD)"
                value={form.yearly_amount_usd}
                onChange={(e) => setField("yearly_amount_usd", e.target.value)}
                disabled={readOnly || saving}
                inputProps={{ min: 0, step: 1 }}
              />
            </Grid>
          </Grid>

          <TextField
            fullWidth
            size="small"
            type="number"
            label="Sort order"
            value={form.sort_order}
            onChange={(e) => setField("sort_order", e.target.value)}
            disabled={readOnly || saving}
            inputProps={{ min: 0, step: 1 }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={form.is_most_popular}
                onChange={(e) => setField("is_most_popular", e.target.checked)}
                disabled={readOnly || saving}
              />
            }
            label="Most popular (pricing page badge)"
          />

          <FormControlLabel
            control={
              <Switch
                checked={form.is_active}
                onChange={(e) => setField("is_active", e.target.checked)}
                disabled={readOnly || saving}
              />
            }
            label="Active (visible on public pricing page)"
          />

          <Typography variant="caption" color="text.secondary" display="block">
            Updated: {formatDisplayDateTime(plan.updated_at)}
          </Typography>
        </Stack>
      </DialogContent>
      {!readOnly ? (
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={22} color="inherit" /> : "Save changes"}
          </Button>
        </DialogActions>
      ) : null}
    </Dialog>
  );
}

export default function VMSuperAdminSubscriptionPlans() {
  const { isSuperAdmin } = useAdminPanel();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editPlan, setEditPlan] = useState(null);

  const loadPlans = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getSubscriptionPlans();
      const body = res.data;
      if (body?.status_code === 200) {
        const list = Array.isArray(body.data) ? body.data : [];
        setPlans([...list].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)));
      } else {
        setPlans([]);
        toast.error(body?.message || "Failed to load subscription plans.");
      }
    } catch (err) {
      setPlans([]);
      toast.error(err?.response?.data?.message || "Subscription plans API unavailable.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const handleSaved = (updated) => {
    if (!updated?.id) {
      loadPlans();
      return;
    }
    setPlans((prev) => {
      const next = prev.map((p) => (p.id === updated.id ? updated : p));
      if (updated.is_most_popular) {
        return next.map((p) =>
          p.id === updated.id ? p : { ...p, is_most_popular: false }
        );
      }
      return next;
    });
  };

  return (
    <Box>
      <SuperAdminPageHeader title="Subscription Plans" />

      {!isSuperAdmin ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Sub Admin can view plans only. Contact a Super Admin to update pricing or plan settings.
        </Alert>
      ) : null}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "primary.main" }}>
                {TABLE_HEADERS.map((h) => (
                  <TableCell key={h} sx={{ color: "primary.contrastText", fontWeight: 700 }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {plans.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={TABLE_HEADERS.length} align="center" sx={{ py: 4, color: "text.secondary" }}>
                    No subscription plans found.
                  </TableCell>
                </TableRow>
              ) : (
                plans.map((plan) => (
                  <TableRow key={plan.id} hover>
                    <TableCell>{plan.sort_order ?? "—"}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {plan.display_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={plan.plan_code} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      {plan.is_unlimited_users ? "Unlimited" : plan.max_users ?? "—"}
                    </TableCell>
                    <TableCell>{formatAmount(plan.monthly_amount_inr, "₹")}</TableCell>
                    <TableCell>{formatAmount(plan.yearly_amount_inr, "₹")}</TableCell>
                    <TableCell>{formatAmount(plan.monthly_amount_usd, "$")}</TableCell>
                    <TableCell>{formatAmount(plan.yearly_amount_usd, "$")}</TableCell>
                    <TableCell>
                      {plan.is_most_popular ? (
                        <Chip label="Popular" size="small" color="primary" />
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={plan.is_active ? "Active" : "Inactive"}
                        size="small"
                        color={plan.is_active ? "success" : "default"}
                        variant={plan.is_active ? "filled" : "outlined"}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={isSuperAdmin ? "Edit plan" : "View plan"}>
                        <IconButton
                          size="small"
                          color="primary"
                          aria-label={isSuperAdmin ? "Edit plan" : "View plan"}
                          onClick={() => setEditPlan(plan)}
                        >
                          {isSuperAdmin ? (
                            <EditOutlinedIcon fontSize="small" />
                          ) : (
                            <VisibilityOutlinedIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <PlanEditDialog
        open={Boolean(editPlan)}
        plan={editPlan}
        readOnly={!isSuperAdmin}
        onClose={() => setEditPlan(null)}
        onSaved={handleSaved}
      />
    </Box>
  );
}
